import { flow, pipe } from 'fp-ts/lib/function';
import request, { gql } from 'graphql-request';
import * as TaskEither from 'fp-ts/TaskEither';
import * as Task from 'fp-ts/Task';
import * as Either from 'fp-ts/Either';
import * as t from 'io-ts';
import { QueryFunction, useQuery } from '@tanstack/react-query';
import { useSiloWeb3 } from '@silo/web3';
import { GraphInterestRates } from './codec';
import { GraphPrerequisites, GraphPrerequisitesError, graphPrerequisites, isNotNullNorUndefined } from '@silo/utils';
import { ChecksumAddress, ClientProvider, GRAPH_ENDPOINT, ProtocolInstance } from '@silo/types';

export const useQueryGraphInterestRates = (siloAddress: ChecksumAddress) => {
  const { protocolInstance, clientProvider } = useSiloWeb3();

  return useQuery({
    // asserting as non-null due to enabled check
    queryKey: [graphMarketInterestRatesKey, protocolInstance!, clientProvider!, siloAddress],
    queryFn: queryGraphInterestRates,
    enabled: isNotNullNorUndefined(protocolInstance) && isNotNullNorUndefined(clientProvider),
  });
};

type InterestRatesError = GraphPrerequisitesError | FetchError | DecodeError;

type FetchError = {
  _tag: 'FetchError';
  error: unknown;
  siloAddress: ChecksumAddress;
  endpoint: string;
  minBlockNumber: number;
};
type DecodeError = { _tag: 'DecodeError'; errors: t.Errors; response: unknown };

export type MarketInterestRates = GraphInterestRates['silo'];

const graphMarketInterestRatesKey = 'graphMarketInterestRates';
type GraphInterestRatesKey = [
  key: typeof graphMarketInterestRatesKey,
  protocolInstance: ProtocolInstance,
  clientProvider: ClientProvider,
  siloAddress: ChecksumAddress
];

const queryGraphInterestRates: QueryFunction<MarketInterestRates, GraphInterestRatesKey> = ({
  queryKey: [, protocolInstance, clientProvider, siloAddress],
}) => fetchGraphInterestRates(clientProvider, protocolInstance, siloAddress)();

const fetchGraphInterestRates = (
  clientProvider: ClientProvider,
  protocolInstance: ProtocolInstance,
  siloAddress: ChecksumAddress
) => {
  return pipe(
    graphPrerequisites(clientProvider, protocolInstance),
    TaskEither.chainW((prerequisites) => fetchQuery(siloAddress, prerequisites)),
    TaskEither.chainW(flow(parseGraphInterestRates, TaskEither.fromEither)),
    TaskEither.map((response) => response.silo),
    TaskEither.fold((error) => {
      throw error;
    }, Task.of)
  );
};

const fetchQuery = (siloAddress: ChecksumAddress, { minBlockNumber, protocolInstance }: GraphPrerequisites) => {
  const endpoint = GRAPH_ENDPOINT[protocolInstance];

  return pipe(
    TaskEither.tryCatch(
      () => request(endpoint, getGraphInterestRatesGql(siloAddress, minBlockNumber)),
      (error): InterestRatesError => {
        return {
          _tag: 'FetchError',
          error,
          siloAddress,
          minBlockNumber,
          endpoint,
        };
      }
    )
  );
};

const parseGraphInterestRates = (response: unknown) => {
  return pipe(
    GraphInterestRates.decode(response),
    Either.mapLeft(
      (errors): DecodeError => ({
        _tag: 'DecodeError',
        errors,
        response,
      })
    )
  );
};

const getGraphInterestRatesGql = (siloAddress: ChecksumAddress, minBlockNumber: number) => {
  return gql`
    query QueryMarketInterestRates($siloAddress: String = "${siloAddress.toLowerCase()}") {
      silo(id: $siloAddress, block: { number_gte: ${minBlockNumber} }) {
        id
        name
        rates {
          side
          token {
            id
            symbol
          }
          interestRateDaily(first: 31, orderBy: day, orderDirection: desc) {
            rateAvg
            day
          }
          interestRateHourly(first: 24, orderBy: hour, orderDirection: desc) {
            hour
            rateAvg
          }
        }
      }
    }
  `;
};
